import { create } from "zustand";
import { authenticatedFetch } from "../lib/authenticatedFetch";
import { V2_ENDPOINT } from "../consts";
import type { PaginatedData } from "../types/pagination";
import {
  DEFAULT_WORKFLOW_RUNS_PAGE_SIZE,
  fetchWorkflowRunById,
  fetchWorkflowRunsPage,
  getWorkflowRunDurationMs,
} from "../lib/workflow/workflowRuns";

// Workflow run type definitions based on API response
export interface WorkflowNodePayload {
  type: string;
  payload: Record<string, unknown>;
  children?: string[] | null;
  loopBody?: string[] | null;
  branch?: "then" | "else" | "resumed" | "timed_out";
  connector?: { id?: string; type: string };
}

export interface WorkflowNodeExecutionState {
  status:
    | "success"
    | "partial"
    | "failure"
    | "pending"
    | "running"
    | "skipped"
    | "cancelled"
    | "waiting"
    | "scheduled"
    | "auto_swapping";
  type?: string;
  input?: Record<string, unknown>;
  result?: Record<string, unknown>;
  connector?: Record<string, unknown>;
  context?: Record<string, unknown>;
  startedAt?: string;
  finishedAt?: string;
  scheduledAt?: string;
  iterations?: Array<Record<string, WorkflowNodeExecutionState>>;
}

export interface BlockExpansionMeta {
  blockId: string;
  blockVersion: number;
  expandedIds: string[];
  entryNodeIds: string[];
  outputSchema: Array<{
    key: string;
    sourceNode: string;
    sourceField: string;
    type?: string;
    description?: string;
  }>;
}

export interface WorkflowRun {
  id: string;
  userId: string;
  workflowId: string;
  workflowName?: string;
  workflowVersion?: number;
  status: "success" | "partial" | "failure" | "pending" | "running" | "cancelled" | "waiting";
  workflowDefinition?: {
    nodes: Record<string, WorkflowNodePayload>;
    blockExpansions?: Record<string, BlockExpansionMeta>;
  };
  executionState?: Record<string, WorkflowNodeExecutionState>;
  startedAt: string;
  finishedAt?: string;
  triggerSource?: string;
  triggeredBy?: string | null;
  totalCu?: string; // Total CU consumed (decimal as string, from LEFT JOIN)
  isSimulation?: boolean;
  isDustTest?: boolean;
}

export interface WorkflowRunsApiResponse {
  code: number;
  message: string;
  data: PaginatedData<WorkflowRun>;
  requestId: string;
}

interface WorkflowRunsStore {
  runs: WorkflowRun[];
  loading: boolean;
  error: Error | null;
  lastLoadedAt: number | null;
  // Pagination state
  page: number;
  hasMore: boolean;
  paginationLimit: number;
  currentWorkflowId: string | undefined;
  currentStatus: WorkflowRun["status"] | undefined;
  loadRuns: (workflowId?: string) => Promise<void>;
  loadRunsPage: (page: number, workflowId?: string, status?: WorkflowRun["status"]) => Promise<void>;
  nextPage: () => Promise<void>;
  prevPage: () => Promise<void>;
  getRunById: (runId: string) => Promise<WorkflowRun>;
  refetch: (workflowId?: string) => Promise<WorkflowRun[]>;
  reset: () => void;
  getDuration: (run: WorkflowRun) => number | undefined;
  cancelRun: (runId: string) => Promise<void>;
  retryRun: (runId: string) => Promise<{ id: string; workflowId: string }>;
}

export const useWorkflowRunsStore = create<WorkflowRunsStore>((set, get) => ({
  runs: [],
  loading: false,
  error: null,
  lastLoadedAt: null,
  page: 0,
  hasMore: false,
  paginationLimit: DEFAULT_WORKFLOW_RUNS_PAGE_SIZE,
  currentWorkflowId: undefined,
  currentStatus: undefined,

  loadRunsPage: async (page: number, workflowId?: string, status?: WorkflowRun["status"]) => {
    const { runs: currentRuns, paginationLimit } = get();

    // Only show loading state if we don't have cached data (SWR pattern)
    set({ loading: currentRuns.length === 0, error: null });

    try {
      const offset = page * paginationLimit;
      const result = await fetchWorkflowRunsPage({ workflowId, limit: paginationLimit, offset, status });
      set({
        runs: result.items || [],
        page,
        hasMore: result.hasMore,
        currentWorkflowId: workflowId,
        currentStatus: status,
        lastLoadedAt: Date.now(),
        loading: false,
      });
    } catch (e) {
      const error = e instanceof Error ? e : new Error("Failed to load workflow runs");
      console.error("Failed to load workflow runs", error);
      set({ error, loading: false });
    }
  },

  loadRuns: async (workflowId?: string) => {
    await get().loadRunsPage(0, workflowId);
  },

  nextPage: async () => {
    const { page, hasMore, currentWorkflowId, currentStatus } = get();
    if (!hasMore) return;
    await get().loadRunsPage(page + 1, currentWorkflowId, currentStatus);
  },

  prevPage: async () => {
    const { page, currentWorkflowId, currentStatus } = get();
    if (page <= 0) return;
    await get().loadRunsPage(page - 1, currentWorkflowId, currentStatus);
  },

  getRunById: async (runId: string): Promise<WorkflowRun> => {
    try {
      const run = await fetchWorkflowRunById(runId);

      // Update the run in the store if it exists
      set(state => ({
        runs: state.runs.map(r => (r.id === runId ? run : r)),
      }));

      return run;
    } catch (e) {
      const error = e instanceof Error ? e : new Error("Failed to get workflow run");
      console.error("Failed to get workflow run", error);
      throw error;
    }
  },

  refetch: async (workflowId?: string): Promise<WorkflowRun[]> => {
    await get().loadRuns(workflowId);
    return get().runs;
  },

  reset: () => {
    set({
      runs: [],
      loading: false,
      error: null,
      lastLoadedAt: null,
      page: 0,
      hasMore: false,
      currentWorkflowId: undefined,
      currentStatus: undefined,
    });
  },

  getDuration: (run: WorkflowRun): number | undefined => {
    if (!run.finishedAt) return undefined;
    return getWorkflowRunDurationMs(run);
  },

  cancelRun: async (runId: string): Promise<void> => {
    const response = await authenticatedFetch(`${V2_ENDPOINT}/v1/runs/${runId}/cancel`, {
      method: "POST",
    });

    if (!response.ok) {
      const errData = await response.json().catch(() => null);
      throw new Error(errData?.message ?? `Failed to cancel run: ${response.statusText}`);
    }

    const data = await response.json();

    if (data.code !== 0) {
      throw new Error(`API error: ${data.message}`);
    }

    // Optimistic status update.
    set(state => ({
      runs: state.runs.map(r => (r.id === runId ? { ...r, status: "cancelled" as const } : r)),
    }));
  },

  retryRun: async (runId: string): Promise<{ id: string; workflowId: string }> => {
    const response = await authenticatedFetch(`${V2_ENDPOINT}/v1/runs/${runId}/retry`, {
      method: "POST",
    });

    if (!response.ok) {
      const errData = await response.json().catch(() => null);
      throw new Error(errData?.message ?? `Failed to retry run: ${response.statusText}`);
    }

    const data = await response.json();

    if (data.code !== 0) {
      throw new Error(`API error: ${data.message}`);
    }

    return { id: data.data.id, workflowId: data.data.workflowId };
  },
}));
